// frontend/src/components/Concierge/Setting/AutomationsSetting.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  Grid
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/**
 * AutomationsSetting (Enhanced UI)
 * - Professional, compact rule cards with collapse/expand
 * - Variable chips for message templates (e.g., {{guest.firstName}})
 * - Trigger-aware inputs (minutes for no-reply, etc.)
 * - Inline live preview of the first send_message action
 * - Duplicate rule, quick add condition/action, inline validation
 */
export default function AutomationsSetting({ initial = [], onSave }) {
  // Fixed toolbar height so the header never jumps
  const HEADER_HEIGHT = 64;

  const defaultRule = useMemo(
    () => ({
      id: `rule_${Date.now()}`,
      name: "New Automation",
      enabled: true,
      trigger: "conversation_started",
      triggerMeta: { countReadAsReply: true, countReactAsReply: true, countThreadViewAsReply: true },
      conditions: [],
      actions: [{ type: "send_message", text: "Thanks for reaching out! 👋" }],
      _collapsed: true,
    }),
    []
  );

  const [rules, setRules] = useState(initial.length ? initial.map(r => ({ ...r, _collapsed: true })) : [defaultRule]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Sync when backend data arrives/changes
  useEffect(() => {
    const next = (Array.isArray(initial) ? initial : []).map(r => ({
      id: r.id || `rule_${Date.now()}`,
      name: r.name || 'New Automation',
      enabled: typeof r.enabled === 'boolean' ? r.enabled : true,
      trigger: r.trigger || 'conversation_started',
      triggerMeta: (() => {
        const tm = r.triggerMeta || {};
        if (typeof tm.countReadAsReply === 'undefined') tm.countReadAsReply = true;
        if (typeof tm.countReactAsReply === 'undefined') tm.countReactAsReply = true;
        if (typeof tm.countThreadViewAsReply === 'undefined') tm.countThreadViewAsReply = true;
        return tm;
      })(),
      conditions: r.conditions || [],
      actions: r.actions || [{ type: 'send_message', text: 'Thanks for reaching out! 👋' }],
      _collapsed: true,
    }));
    console.log('[AutomationsSetting] initial from backend:', initial);
    setRules(next.length ? next : [defaultRule]);
  }, [initial, defaultRule]);

  const triggerOptions = [
    { id: "conversation_started", label: "When a conversation starts" },
    { id: "guest_first_message", label: "On guest's first message" },
    { id: "guest_checked_in", label: "When a guest checks in" },
    { id: "no_reply_minutes", label: "If no reply for N minutes" },
    { id: "after_checkout", label: "After checkout" },
    { id: "tag_added", label: "When a tag is added" },
  ];

  const conditionOptions = [
    { id: "channel_is", label: "Channel is…" },
    { id: "language_is", label: "Language is…" },
    { id: "has_tag", label: "Has tag…" },
    { id: "message_contains", label: "Message contains…" },
    { id: "time_between", label: "Time between…" },
    { id: "checkin_source_is", label: "Check-in source is…" },
  ];

  const checkinSourceOptions = [
    { id: 'manual',    label: 'Manual (staff)' },
    { id: 'reference', label: 'Reference (booking link)' },
    { id: 'kiosk',     label: 'Kiosk / tablet' },
    { id: 'api',       label: 'API / integration' },
  ];

  const actionOptions = [
    { id: "send_message", label: "Send message" },
    { id: "apply_tag", label: "Apply tag" },
    { id: "assign_to_user", label: "Assign to staff" },
    { id: "close_conversation", label: "Close conversation" },
  ];

  // Common template variables for personalization
  const templateVars = [
    { key: "{{guest.name}}", label: "Guest name" },
    { key: "{{reservation.checkIn}}", label: "Check-in (date)" },
    { key: "{{reservation.checkOut}}", label: "Check-out (date)" },
    { key: "{{property.name}}", label: "Property name" },
    { key: "{{room.number}}", label: "Room number" },
  ];

  const updateRule = (idx, patch) =>
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRule = () => setRules((prev) => [...prev, { ...defaultRule, id: `rule_${Date.now()}`, _collapsed: true }]);
  const removeRule = (idx) => setRules((prev) => prev.filter((_, i) => i !== idx));
  const duplicateRule = (idx) =>
    setRules((prev) => {
      const src = prev[idx];
      const copy = JSON.parse(JSON.stringify(src));
      copy.id = `rule_${Date.now()}`;
      copy.name = `${src.name} (Copy)`;
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });

  const addCondition = (idx) =>
    updateRule(idx, {
      conditions: [...(rules[idx].conditions || []), { type: "channel_is", value: "" }],
    });

  const updateCondition = (rIdx, cIdx, patch) =>
    setRules((prev) =>
      prev.map((r, i) =>
        i === rIdx
          ? {
              ...r,
              conditions: r.conditions.map((c, j) => (j === cIdx ? { ...c, ...patch } : c)),
            }
          : r
      )
    );

  const removeCondition = (rIdx, cIdx) =>
    setRules((prev) =>
      prev.map((r, i) =>
        i === rIdx ? { ...r, conditions: r.conditions.filter((_, j) => j !== cIdx) } : r
      )
    );

  const addAction = (idx) =>
    updateRule(idx, {
      actions: [...(rules[idx].actions || []), { type: "send_message", text: "" }],
    });

  const updateAction = (rIdx, aIdx, patch) =>
    setRules((prev) =>
      prev.map((r, i) =>
        i === rIdx
          ? { ...r, actions: r.actions.map((a, j) => (j === aIdx ? { ...a, ...patch } : a)) }
          : r
      )
    );

  const removeAction = (rIdx, aIdx) =>
    setRules((prev) =>
      prev.map((r, i) =>
        i === rIdx ? { ...r, actions: r.actions.filter((_, j) => j !== aIdx) } : r
      )
    );

  // Insert a template variable into a specific action text
  const insertVar = (rIdx, aIdx, variable) => {
    const current = rules[rIdx]?.actions?.[aIdx]?.text || "";
    updateAction(rIdx, aIdx, { text: (current + (current ? " " : "") + variable).trim() });
  };

  const validate = () => {
    const errs = {};
    rules.forEach((r, i) => {
      if (!r.name || !r.name.trim()) {
        errs[i] = errs[i] || {}; errs[i].name = "Name is required";
      }
      if (r.trigger === "no_reply_minutes") {
        const m = Number(r?.triggerMeta?.minutes || 0);
        if (!m || m < 1) { errs[i] = errs[i] || {}; errs[i].minutes = "Enter minutes (≥1)"; }
      }
      const msgActions = (r.actions || []).filter(a => a.type === "send_message");
      if (!msgActions.length) {
        errs[i] = errs[i] || {}; errs[i].actions = "At least one action is required";
      } else {
        msgActions.forEach((a, j) => {
          if (!a.text || !a.text.trim()) {
            errs[i] = errs[i] || {}; errs[i][`a_${j}`] = "Message text is required";
          }
        });
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = rules.map((r) => ({
        id: r.id,
        name: r.name?.trim() || "Untitled",
        enabled: !!r.enabled,
        trigger: r.trigger,
        triggerMeta: r.triggerMeta || {},
        conditions: (r.conditions || []).map((c) => ({ type: c.type, value: c.value })),
        actions: (r.actions || []).map((a) => ({
          type: a.type,
          text: a.text,
          tag: a.tag,
          assignee: a.assignee,
          delayMinutes: a.delayMinutes,
        })),
      }));
      console.log("[AutomationsSetting] Saving payload:", payload);
      await onSave?.(payload);
    } finally {
      setSaving(false);
    }
  };

  // Simple preview renderer for first send_message action
  const renderPreviewText = (rule) => {
    const msg = (rule.actions || []).find(a => a.type === "send_message");
    if (!msg) return "";
    const sample = {
      guest: { fullName: "Guest" },
      reservation: { checkIn: "2025-09-12", checkOut: "2025-09-15" },
      property: { name: "Eightfold Urban Resort" },
      room: { number: "201" },
    };
    let t = msg.text || "";
    Object.entries({
      "{{guest.name}}": sample.guest.fullName,
      "{{reservation.checkIn}}": sample.reservation.checkIn,
      "{{reservation.checkOut}}": sample.reservation.checkOut,
      "{{property.name}}": sample.property.name,
      "{{room.number}}": sample.room.number,
    }).forEach(([k, v]) => {
      t = t.split(k).join(v);
    });
    return t;
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">🤖 Automations</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={addRule}>
            New automation
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save all"}
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create rules that automatically perform actions when certain events occur. Each rule
        contains a <b>trigger</b>, optional <b>conditions</b>, and one or more <b>actions</b>.
      </Typography>

      <Stack spacing={2}>
        {rules.map((rule, rIdx) => {
          const rErr = errors[rIdx] || {};
          const preview = renderPreviewText(rule);
          return (
            <Paper key={rule.id} variant="outlined" sx={{ p: 0, maxWidth: 1100, overflow: "hidden", position: "relative" }}>
              {/* Header - fixed height */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 2,
                  height: HEADER_HEIGHT,
                  minHeight: HEADER_HEIGHT,
                  position: "sticky",
                  top: 0,
                  backgroundColor: "background.paper",
                  zIndex: 1,
                  borderBottom: rule._collapsed ? "none" : "1px solid",
                  borderColor: "divider",
                }}
              >
                <TextField
                  size="small"
                  label="Name"
                  value={rule.name}
                  error={!!rErr.name}
                  onChange={(e) => updateRule(rIdx, { name: e.target.value })}
                  sx={{
                    minWidth: 260,
                    "& .MuiFormHelperText-root": { display: "none" }, // ensure no helper text area expands header
                  }}
                  InputProps={{ sx: { height: 40 } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!rule.enabled}
                      onChange={(e) => updateRule(rIdx, { enabled: e.target.checked })}
                    />
                  }
                  label="Enabled"
                />
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title={rule._collapsed ? "Expand" : "Collapse"}>
                  <IconButton onClick={() => updateRule(rIdx, { _collapsed: !rule._collapsed })}>
                    {rule._collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplicate">
                  <IconButton onClick={() => duplicateRule(rIdx)}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={() => removeRule(rIdx)} aria-label="Delete rule">
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              {!rule._collapsed && (
                <>
                  <Divider sx={{ my: 1.5 }} />

                  {/* Surface header validation inside the body so it doesn't resize the header */}
                  {rErr.name && (
                    <Box sx={{ px: 2, pb: 1 }}>
                      <Alert severity="warning" sx={{ py: 0.5 }}>{rErr.name}</Alert>
                    </Box>
                  )}

                  {/* Trigger */}
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, px: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: 80 }}>
                      Trigger
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={rule.trigger}
                      onChange={(e) => updateRule(rIdx, { trigger: e.target.value })}
                      sx={{ minWidth: 320 }}
                    >
                      {triggerOptions.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {rule.trigger === "no_reply_minutes" && (
                      <TextField
                        size="small"
                        label="Minutes"
                        type="number"
                        error={!!rErr.minutes}
                        helperText={rErr.minutes}
                        inputProps={{ min: 1, max: 120 }}
                        sx={{ width: 140 }}
                        value={rule?.triggerMeta?.minutes || ""}
                        onChange={(e) =>
                          updateRule(rIdx, {
                            triggerMeta: { ...rule.triggerMeta, minutes: Number(e.target.value || 0) },
                          })
                        }
                      />
                    )}
                    {rule.trigger === "no_reply_minutes" && (
                      <>
                        <FormControlLabel
                          sx={{ ml: 1 }}
                          control={
                            <Switch
                              size="small"
                              checked={typeof rule?.triggerMeta?.countReadAsReply === 'boolean' ? rule.triggerMeta.countReadAsReply : true}
                              onChange={(e) =>
                                updateRule(rIdx, { triggerMeta: { ...rule.triggerMeta, countReadAsReply: e.target.checked } })
                              }
                            />
                          }
                          label="Count read as reply"
                        />
                        <FormControlLabel
                          sx={{ ml: 1 }}
                          control={
                            <Switch
                              size="small"
                              checked={typeof rule?.triggerMeta?.countReactAsReply === 'boolean' ? rule.triggerMeta.countReactAsReply : true}
                              onChange={(e) =>
                                updateRule(rIdx, { triggerMeta: { ...rule.triggerMeta, countReactAsReply: e.target.checked } })
                              }
                            />
                          }
                          label="Count reaction as reply"
                        />
                        <FormControlLabel
                          sx={{ ml: 1 }}
                          control={
                            <Switch
                              size="small"
                              checked={typeof rule?.triggerMeta?.countThreadViewAsReply === 'boolean' ? rule.triggerMeta.countThreadViewAsReply : true}
                              onChange={(e) =>
                                updateRule(rIdx, { triggerMeta: { ...rule.triggerMeta, countThreadViewAsReply: e.target.checked } })
                              }
                            />
                          }
                          label="Count thread view as reply"
                        />
                      </>
                    )}
                  </Stack>

                  {/* Conditions */}
                  <Stack spacing={1} sx={{ mb: 2, px: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" sx={{ minWidth: 80 }}>
                        Conditions
                      </Typography>
                      <Chip label="Add condition" onClick={() => addCondition(rIdx)} size="small" variant="outlined" />
                    </Stack>
                    {rule.conditions.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        (No conditions — rule always applies)
                      </Typography>
                    )}
                    {rule.conditions.map((c, cIdx) => (
                      <Stack key={`${rule.id}_cond_${cIdx}`} direction="row" spacing={1} alignItems="center">
                        <TextField
                          select
                          size="small"
                          value={c.type}
                          onChange={(e) => updateCondition(rIdx, cIdx, { type: e.target.value })}
                          sx={{ minWidth: 220 }}
                        >
                          {conditionOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        {c.type === 'checkin_source_is' ? (
                          <TextField
                            select
                            size="small"
                            label="Source"
                            value={c.value || ''}
                            onChange={(e) => updateCondition(rIdx, cIdx, { value: e.target.value })}
                            sx={{ minWidth: 260 }}
                          >
                            {checkinSourceOptions.map((o) => (
                              <MenuItem key={o.id} value={o.id}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            size="small"
                            placeholder="Value"
                            value={c.value || ''}
                            onChange={(e) => updateCondition(rIdx, cIdx, { value: e.target.value })}
                            sx={{ minWidth: 260 }}
                          />
                        )}
                        <IconButton onClick={() => removeCondition(rIdx, cIdx)} aria-label="Delete condition">
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>

                  {/* Actions */}
                  <Stack spacing={1} sx={{ px: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" sx={{ minWidth: 80 }}>
                        Actions
                      </Typography>
                      <Chip label="Add action" onClick={() => addAction(rIdx)} size="small" variant="outlined" />
                    </Stack>
                    {(rule.actions || []).map((a, aIdx) => (
                      <Stack key={`${rule.id}_act_${aIdx}`} direction="row" spacing={1} alignItems="center">
                        <TextField
                          select
                          size="small"
                          value={a.type}
                          onChange={(e) => updateAction(rIdx, aIdx, { type: e.target.value })}
                          sx={{ minWidth: 220 }}
                        >
                          {actionOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        {a.type === "send_message" && (
                          <>
                            <Grid container spacing={1} alignItems="flex-start" sx={{ flex: 1 }}>
                              <Grid item xs={12} md={8}>
                                <Box sx={{ maxWidth: 600 }}>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Message text"
                                    value={a.text || ""}
                                    error={!!rErr[`a_${aIdx}`]}
                                    helperText={rErr[`a_${aIdx}`] || ""}
                                    onChange={(e) => updateAction(rIdx, aIdx, { text: e.target.value })}
                                    multiline
                                  />
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 1, overflowX: "auto", flexWrap: "nowrap", pr: 1 }}
                                  >
                                    {templateVars.map((v) => (
                                      <Chip
                                        key={v.key}
                                        label={v.key}
                                        size="small"
                                        onClick={() => insertVar(rIdx, aIdx, v.key)}
                                      />
                                    ))}
                                  </Stack>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Delay (min)"
                                  value={a.delayMinutes || ""}
                                  onChange={(e) => updateAction(rIdx, aIdx, { delayMinutes: Number(e.target.value || 0) })}
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </>
                        )}

                        {a.type === "apply_tag" && (
                          <TextField
                            size="small"
                            placeholder="Tag"
                            value={a.tag || ""}
                            onChange={(e) => updateAction(rIdx, aIdx, { tag: e.target.value })}
                            sx={{ minWidth: 240 }}
                          />
                        )}
                        {a.type === "assign_to_user" && (
                          <TextField
                            size="small"
                            placeholder="Assignee (email or id)"
                            value={a.assignee || ""}
                            onChange={(e) => updateAction(rIdx, aIdx, { assignee: e.target.value })}
                            sx={{ minWidth: 260 }}
                          />
                        )}

                        <IconButton onClick={() => removeAction(rIdx, aIdx)} aria-label="Delete action">
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>

                  {/* Live Preview */}
                  <Box sx={{ mt: 2, px: 2, pb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Preview</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, background: "#fafafa", maxHeight: 140, overflowY: "auto" }}>
                      <Typography variant="body2">{preview || "(No message yet…)"}</Typography>
                    </Paper>
                  </Box>

                  {rErr.actions && (
                    <Box sx={{ mt: 1, px: 2, pb: 2 }}>
                      <Alert severity="warning">{rErr.actions}</Alert>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}